import { KeyValueCustomSelect, Text } from "@smartsuite/react-ui/lib";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import {
  StyledTitle,
  StyledTopLineOuter,
} from "../../../../../../../../components/CaptionBlock/styles";
import { translateIfPresent } from "../../../../../../../../locales/i18n";
import { useTheme } from "../../../../../../../../stores";
import { useRootStore } from "../../../../../../../../stores/hooks";
import type { InputCandidate } from "../../../../../../../../types/candidate";
import { getLocalCssColorVariables } from "../../../../../../../../utils/cssVariablesUtils";
import { StyledContainerWrapper } from "../../../style";
import { StartingFrom, TimeEditor } from "./components";
import { MonthDay } from "./components/MonthDay/MonthDay";
import { NextCallsInfo } from "./components/NextCallsInfo/NextCallsInfo";
import { RepeatEvery } from "./components/RepeatEvery/RepeatEvery";
import { WeekDay } from "./components/WeekDay/WeekDay";
import { intervalTypes } from "./interval-type";
import { useRepeatedSchedule } from "./repeated-schedule.hook";
import { useScheduleConverter } from "./schedule-converter";
import * as S from "./style";

interface Props {
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
}

export const ScheduleEditor = observer<Props>(({ input, onChange }) => {
  const theme = useTheme();

  const [open, setOpen] = useState<boolean>(false);
  const [selectedIntervalType, setSelectedIntervalType] =
    useState<string>("weeks");

  const { convert } = useScheduleConverter();
  const { scheduleStore } = useRootStore();

  const { storeDefaultSchedule, getOldScheduleType, setScheduleType } =
    useRepeatedSchedule();

  useEffect(() => {
    if (
      input.value?.values !== undefined &&
      input.value?.values.length > 0 &&
      input.value.values[0].value.oneofKind === "schedule"
    ) {
      convert(input.value.values[0].value.schedule);
    } else {
      storeDefaultSchedule();
    }
    setSelectedIntervalType(getOldScheduleType());
  }, []);

  useEffect(() => {
    if (scheduleStore.schedule !== undefined) {
      const newInput: InputCandidate = {
        ...input,
        value: {
          values: [
            {
              value: {
                oneofKind: "schedule",
                schedule: {
                  cron: "",
                  nextCalls: [],
                  metadata: {},
                  schedule: scheduleStore.schedule,
                },
              },
            },
          ],
        },
      };
      onChange(newInput);
    }
  }, [scheduleStore.schedule]);

  const onOpenedChange = (e: boolean): void => {
    setOpen(e);
  };

  return (
    <S.StyledShedule>
      <StyledContainerWrapper>
        <StyledTopLineOuter>
          <StyledTitle>{t("scheduled-trigger.every")}</StyledTitle>
        </StyledTopLineOuter>

        <S.Row top={selectedIntervalType === "months" ? "-68px" : "0px"}>
          <S.Inline>
            <RepeatEvery isDisabled={selectedIntervalType === "one-time"} />
          </S.Inline>
          <S.Space />
          <S.Space />
          <S.Space />
          <S.Inline width="95px">
            <KeyValueCustomSelect
              my="left top+2"
              state={open ? "active" : "default"}
              disabled={false}
              value={selectedIntervalType}
              showClear={false}
              placeholderArrowStyle="filled-dark"
              options={intervalTypes.map((v) => ({
                value: v.value,
                label: translateIfPresent(
                  `scheduled-trigger.interval-${v.value}`,
                  v.label,
                ),
              }))}
              className={open ? "container-is-open" : ""}
              portalStyles={getLocalCssColorVariables(theme)}
              onOpenedChange={onOpenedChange}
              autoWidthList={true}
              dropdownMinWidth={90}
              onChange={(value) => {
                setSelectedIntervalType(String(value));
                setScheduleType(String(value));
              }}
            />
          </S.Inline>
          <S.Space />
          <S.Space />
          {selectedIntervalType === "weeks" && (
            <>
              <Text weight="medium">{t("scheduled-trigger.on")}</Text>

              <WeekDay />
            </>
          )}
          {selectedIntervalType === "months" && (
            <>
              <S.Cell>
                <StyledTitle> {t("scheduled-trigger.on")}</StyledTitle>
              </S.Cell>

              <MonthDay />
            </>
          )}

          <S.Cell>
            {!["one-time", "hours"].includes(selectedIntervalType) && (
              <>
                <Text weight="medium"> {t("scheduled-trigger.at")}</Text>

                <S.Space />
                <S.Space />
                <TimeEditor />
              </>
            )}
          </S.Cell>
        </S.Row>

        <S.VerticalSpace height="16" />

        {
          <S.Row>
            <StartingFrom title={t("scheduled-trigger.starting-from")} />
            {["one-time", "hours"].includes(selectedIntervalType) && (
              <>
                <S.Space />
                <S.Space />
                <S.Space />
                <TimeEditor title={t("scheduled-trigger.At")} />
              </>
            )}
            {selectedIntervalType !== "one-time" && <NextCallsInfo />}
          </S.Row>
        }
      </StyledContainerWrapper>
    </S.StyledShedule>
  );
});
